import { useVersionChangeCell } from "@/pages/roster-create/context/VersionChangesContext";
import { VersionChangeMark } from "@/pages/roster-create/components/VersionChangeMark";
import type { RosterChangeCell } from "@/services/roster/changeNotices";
import { getScheduleShiftCodeFontSize } from "@utils/fontSizeUtils";
import { cn } from "@utils/cn.ts";
import ShiftCode from "@/components/shift/ShiftCode";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import { useEffect, useState } from "react";
import AssignmentGroupBar from "@/pages/roster-create/components/shift-code/assignment-group-bar";
import type { AssignmentStartInfo } from "@/services/assignment";
import type { ShiftCodeStatusIndicator } from "@/pages/roster-create/components/shift-code/shift-code-cell";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { X } from "lucide-react";

const EMPTY_SHIFT_CELL_BG = "#F0F0F2";

function TdShiftCodeButtonView({
  day,
  shift,
  isEveryFifth,
  prefix = "",
  nurseId = "",
  nurseName = "",
  nursesIndex = 0,
  assignmentStart = null,
  statusIndicator = null,
  issuedIndicators,
  reissueCell,
}: {
  reissueCell?: RosterChangeCell;
  issuedIndicators?: {
    readonly band: {
      readonly label: string;
      readonly color: string;
      readonly spanDays: number;
    } | null;
    readonly statusLabel?: string;
    readonly rowSpacing?: {
      readonly status: boolean;
      readonly assignment: boolean;
    };
  };
  day: number;
  shift:
    | {
        shift_id: string;
        name: string;
        color: string;
        default_shift?: string;
        shift_gb?: string;
      }
    | undefined;
  isEveryFifth?: boolean;
  prefix?: string;
  nurseId?: string;
  nurseName?: string;
  nursesIndex?: number;
  /** 기간배정(병동이동 등) — 시작 셀에서만 */
  assignmentStart?: AssignmentStartInfo | null;
  statusIndicator?: ShiftCodeStatusIndicator | null;
}) {
  void isEveryFifth;
  const columnKey = `${prefix}day${day}`;
  const displayShiftId = shift?.shift_id ?? "-";
  const fontSize = getScheduleShiftCodeFontSize(displayShiftId);
  const setHoveredFocus = useRosterFocusStore((s) => s.setHoveredFocus);
  const clearHoveredFocus = useRosterFocusStore((s) => s.clearHoveredFocus);
  const setSelectedFocus = useRosterFocusStore((s) => s.setSelectedFocus);
  const clearSelectedFocus = useRosterFocusStore((s) => s.clearSelectedFocus);
  const isResignationStatus = statusIndicator?.reason === "퇴사";
  const resignationRangePosition = statusIndicator?.rangePosition ?? "single";
  const shouldOpenResignationPopover =
    isResignationStatus &&
    (resignationRangePosition === "single" ||
      resignationRangePosition === "start");
  const [isResignationPopoverOpen, setIsResignationPopoverOpen] = useState(
    shouldOpenResignationPopover
  );

  useEffect(() => {
    if (shouldOpenResignationPopover) {
      setIsResignationPopoverOpen(true);
    }
  }, [shouldOpenResignationPopover, statusIndicator?.title]);

  const versionCell = useVersionChangeCell(String(nurseId), day);
  const shiftBgColor = shift?.color ?? EMPTY_SHIFT_CELL_BG;
  const hasIssuedStatusSpace =
    issuedIndicators?.rowSpacing?.status ??
    Boolean(issuedIndicators?.statusLabel);
  const hasIssuedAssignmentSpace =
    issuedIndicators?.rowSpacing?.assignment ?? Boolean(issuedIndicators?.band);

  const handleCellMouseEnter = () => {
    setHoveredFocus({ column: columnKey, nurseId: String(nurseId) });
  };
  const handleCellMouseLeave = () => {
    clearHoveredFocus();
  };

  return (
    <div data-column={columnKey}>
      <div
        className={cn(
          "p-[3px] relative flex items-center",
          hasIssuedStatusSpace && "pt-5",
          hasIssuedAssignmentSpace && "pb-6"
        )}
        data-roster-shift-cell=""
      >
        <VersionChangeMark nurseId={String(nurseId)} day={day} />
        {isResignationStatus && (
          <Popover
            open={isResignationPopoverOpen}
            onOpenChange={(open) => {
              if (open) setIsResignationPopoverOpen(true);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className={cn(
                  "absolute inset-y-[2px] z-[4] cursor-pointer border-y-2 border-amber-400 bg-transparent p-0",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-300 focus-visible:ring-offset-1",
                  resignationRangePosition === "single" &&
                    "left-[1px] right-[1px] rounded-md border-x-2",
                  resignationRangePosition === "start" &&
                    "left-[1px] right-[-4px] rounded-l-md border-l-2",
                  resignationRangePosition === "middle" &&
                    "left-[-4px] right-[-4px]",
                  resignationRangePosition === "end" &&
                    "left-[-4px] right-[1px] rounded-r-md border-r-2"
                )}
                data-cell-status-ring={statusIndicator.reason}
                aria-label={`${statusIndicator.title}. 근무표 재생성 권장 안내`}
                onClick={(event) => event.stopPropagation()}
              >
                {(resignationRangePosition === "single" ||
                  resignationRangePosition === "start") && (
                  <span className="absolute -top-3 left-1 rounded-[4px] border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold leading-none text-amber-800 shadow-sm">
                    퇴사
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              side="top"
              className="w-[280px] p-0"
              onInteractOutside={(event) => event.preventDefault()}
              onEscapeKeyDown={(event) => event.preventDefault()}
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">
                    {nurseName || "해당 근무자"} 퇴사 예정입니다.
                  </p>
                  <p className="mt-1 text-xs leading-5 text-slate-600">
                    {statusIndicator.title} 이후 근무표 재생성을 권장합니다.
                  </p>
                </div>
                <button
                  type="button"
                  className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  aria-label="퇴사 안내 닫기"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsResignationPopoverOpen(false);
                  }}
                >
                  <X className="size-4" aria-hidden="true" />
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {assignmentStart && (
          <AssignmentGroupBar
            key={`${nursesIndex}-${day}-asgn-${assignmentStart.reason}-${assignmentStart.startDay}`}
            group={assignmentStart}
          />
        )}
        {issuedIndicators?.statusLabel && (
          <span
            className="absolute right-0 top-0 z-10 max-w-full rounded bg-background px-0.5 text-center text-[11px] font-semibold leading-[10px] break-keep text-foreground"
            data-testid="roster-cell-status"
          >
            {issuedIndicators.statusLabel}
          </span>
        )}
        {issuedIndicators?.band && (
          <span
            role="img"
            aria-label={issuedIndicators.band.label}
            title={issuedIndicators.band.label}
            className="absolute bottom-1 left-[3px] z-10 truncate border-b-2 bg-background px-1 text-center text-[12px] font-semibold leading-4"
            style={{
              width: `calc(${issuedIndicators.band.spanDays} * 100% - 6px)`,
              color: issuedIndicators.band.color,
            }}
            data-testid="roster-assignment-band"
          >
            {issuedIndicators.band.label}
          </span>
        )}
        <div className={cn("d-f jc-c ai-c h-100p ")}>
          <button
            type="button"
            className={cn(
              "rounded-[5px]",
              reissueCell && "roster-reissue-code"
            )}
            data-reissue-cell={reissueCell ? day : undefined}
            title={[
              shift?.name ?? "미배정",
              versionCell
                ? `${versionCell.nurse_name || nurseId} · ${versionCell.date} · ${versionCell.before?.shift_id || "빈 칸"} → ${versionCell.after?.shift_id || "빈 칸"}`
                : undefined,
              issuedIndicators?.statusLabel,
              issuedIndicators?.band?.label,
              reissueCell
                ? `변경 ${reissueCell.before?.shift_id || "빈 칸"} → ${reissueCell.after?.shift_id || "빈 칸"}`
                : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
            aria-label={[
              nurseName,
              `${day}일`,
              displayShiftId,
              issuedIndicators?.statusLabel,
              issuedIndicators?.band?.label,
              reissueCell
                ? `변경 ${reissueCell.before?.shift_id || "빈 칸"} → ${reissueCell.after?.shift_id || "빈 칸"}`
                : undefined,
            ]
              .filter(Boolean)
              .join(" · ")}
            data-nurse-id={String(nurseId)}
            data-day={day}
            data-shift-id={shift?.shift_id ?? displayShiftId}
            data-default-shift={shift?.default_shift ?? ""}
            data-shift-gb={shift?.shift_gb ?? ""}
            {...(statusIndicator
              ? { "data-cell-status-reason": statusIndicator.reason }
              : {})}
            style={{ fontSize }}
            onMouseEnter={handleCellMouseEnter}
            onMouseLeave={handleCellMouseLeave}
            onClick={(e) => {
              e.stopPropagation();
              const nid = String(nurseId);
              const st = useRosterFocusStore.getState();
              if (
                st.selectedColumn === columnKey &&
                st.selectedNurseId === nid
              ) {
                clearSelectedFocus();
              } else {
                setSelectedFocus({
                  column: columnKey,
                  nurseId: nid,
                });
              }
            }}
          >
            <ShiftCode code={displayShiftId} color={shiftBgColor}>
              {statusIndicator && (
                <>
                  {!isResignationStatus && (
                    <>
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 rounded-[5px] ring-2 ring-rose-500 ring-offset-1 ring-offset-white"
                        data-cell-status-ring={statusIndicator.reason}
                      />
                      <span
                        aria-hidden="true"
                        className="pointer-events-none absolute -right-1 -top-1 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-rose-600 px-0.5 text-[9px] font-bold leading-none text-white shadow-sm"
                        data-cell-status-reason={statusIndicator.reason}
                      >
                        {statusIndicator.label}
                      </span>
                    </>
                  )}
                </>
              )}
            </ShiftCode>
          </button>
          <div className="errorShift">
            <div className="errorBadge"></div>
            <ul className="errorMessage"></ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TdShiftCodeButtonView;
